import { ReactNode } from 'react';

import { CodeEditor } from '@@/CodeEditor';

import { HelmRelease } from '../../types';
import { useManifestFromStackID } from '../useManifestFromStackID';

import { DiffViewMode } from './DiffControl';
import { DiffViewSection } from './DiffViewSection';
import { SelectedRevisionNumber, CompareRevisionNumberFetched } from './types';

type Props = {
  manifest: string;
  selectedRevisionNumber: SelectedRevisionNumber;
  diffViewMode: DiffViewMode;
  compareManifest?: string;
  compareRevisionNumberFetched?: CompareRevisionNumberFetched;
  isCompareReleaseLoading: boolean;
  isCompareReleaseError: boolean;
  diffControl: ReactNode;
  stackID?: number;
  release?: HelmRelease;
};

export function ManifestDetails({
  manifest,
  selectedRevisionNumber,
  diffViewMode,
  compareManifest,
  compareRevisionNumberFetched,
  isCompareReleaseLoading,
  isCompareReleaseError,
  diffControl,
  stackID,
  release,
}: Props) {
  const gitManifestQuery = useManifestFromStackID(stackID, release);
  const gitManifest = gitManifestQuery.data?.manifest;

  return (
    <>
      {diffControl}
      {diffViewMode === 'view' ? (
        <CodeEditor
          id="helm-manifest"
          type="yaml"
          data-cy="helm-manifest"
          value={manifest}
          readonly
          fileName={`Revision #${selectedRevisionNumber}`}
          placeholder="No manifest found"
          height="60vh"
        />
      ) : (
        <DiffViewSection
          isCompareReleaseLoading={isCompareReleaseLoading}
          isCompareReleaseError={isCompareReleaseError}
          originalTitle={getOriginalTitle()}
          modifiedTitle={`Revision #${selectedRevisionNumber}`}
          newText={manifest}
          originalText={getOriginalText()}
          placeholder={getPlaceholder()}
          id="helm-manifest-diff-viewer"
          data-cy="helm-manifest-diff-viewer"
        />
      )}
    </>
  );

  function getOriginalText() {
    if (diffViewMode === 'git') {
      return gitManifest ?? '';
    }
    return compareManifest ?? '';
  }

  function getOriginalTitle() {
    if (diffViewMode === 'git') {
      return 'Git manifest preview';
    }
    return compareRevisionNumberFetched
      ? `Revision #${compareRevisionNumberFetched}`
      : 'No revision selected';
  }

  function getPlaceholder() {
    if (diffViewMode === 'git') {
      if (gitManifestQuery.isLoading) {
        return 'Loading git manifest preview (this may take a minute)...';
      }
      if (gitManifestQuery.isError) {
        return `Error loading git manifest: ${
          gitManifestQuery.error?.message || 'Unknown error'
        }`;
      }
    }
    return 'No manifest found';
  }
}
